import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";

import { ConfirmPage } from "./confirm-page.js";
import { DataPending } from "./data-pending.js";
import { tenantUserUrls } from "./urls.js";

/** 引き継ぎ先の候補（<c>GET /api/tenants/{契約}/users/contractor</c>。サーバの ApiContractorTransferResponse）。 */
export interface ContractorTransfer {
    readonly tenantId: number;
    readonly tenantTitle: string;
    readonly candidates: readonly { readonly userName: string; readonly email: string }[];
}

/** 引き継ぎの確認（<c>GET …/contractor/confirm</c>。サーバの ContractorConfirmViewModel と同じ欄）。 */
export interface ContractorConfirm {
    readonly tenantId: number;
    readonly email: string;
    readonly userName: string;
    readonly confirmText: string;
}

/**
 * 契約者の引き継ぎ先を選ぶ。
 * <b>誰が候補になるかはサーバが決める</b>（招待中の人と自分は出ない）。
 */
export function ContractorPage({ tenantId }: { readonly tenantId: number }): ReactElement {
    usePageMeta("ご契約者の変更");
    const data = useApiData<ContractorTransfer>(tenantUserUrls.api.contractor(tenantId));

    if (data.state !== "ready") {
        return <DataPending data={data} />;
    }

    const transfer = data.value;
    return (
        <div className="page max-w-lg">
            {/* 契約情報へ。ページごと移る */}
            <p className="mb-4"><a href={tenantUserUrls.tenantDetail(transfer.tenantId)} className="btn-secondary btn-sm">契約情報へ戻る</a></p>
            <div className="panel">
                <h1 className="page-title">ご契約者の変更</h1>
                <p className="page-lead">{transfer.tenantTitle}</p>

                {transfer.candidates.length === 0
                    ? (
                        <div className="empty-state">
                            <p className="text-slate-500">
                                引き継げる方がいません。<br />
                                先に利用者を招待し、登録を済ませていただく必要があります。
                            </p>
                        </div>
                    )
                    : (
                        <ul className="divide-y divide-slate-200">
                            {transfer.candidates.map((candidate) => (
                                <li key={candidate.email} className="flex items-center justify-between gap-3 py-3">
                                    <div className="min-w-0">
                                        <div className="truncate font-medium">{candidate.userName}</div>
                                        <div className="truncate text-sm text-slate-500">{candidate.email}</div>
                                    </div>
                                    <Link
                                        className="btn-secondary btn-sm shrink-0"
                                        to={tenantUserUrls.contractorConfirm(transfer.tenantId, candidate.email)}
                                    >
                                        この方に引き継ぐ
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
            </div>
        </div>
    );
}

/**
 * 最後の確認はブラウザの確認ダイアログで行う（2026-10-10 に利用者が決めた。解約と同じ）。
 * 引き継ぐと、自分ではご契約の変更も解約もできなくなる。
 */
export const handoverDialog = (name: string, email: string): string =>
    `「${name}（${email}）」にご契約者を引き継ぎます。引き継ぐと、ご契約の変更や解約はできなくなります。よろしいですか？`;

/** 引き継ぎの確認。<b>引き継いだ本人はもう契約者ではない</b>ため、サーバは契約一覧へ戻す。 */
export function ContractorConfirmPage({ tenantId, id }: {
    readonly tenantId: number;
    readonly id: string;
}): ReactElement {
    usePageMeta("ご契約者の変更の確認");
    const data = useApiData<ContractorConfirm>(tenantUserUrls.api.contractorConfirm(tenantId, id));

    if (data.state !== "ready") {
        return <DataPending data={data} />;
    }

    const target = data.value;
    return (
        <ConfirmPage
            title="ご契約者の変更"
            lead={`${target.userName}（${target.email}）へ引き継ぎます。`}
            confirmText={target.confirmText}
            action={tenantUserUrls.api.contractorConfirm(target.tenantId, target.email)}
            cancel={tenantUserUrls.contractor(target.tenantId)}
            submit={{ label: "引き継ぐ", className: "btn-primary" }}
            dialog={handoverDialog(target.userName, target.email)}
        />
    );
}
